import { useEffect, useState } from "react";
import { Save, Plus, Trash2, GripVertical } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MultiImageUpload } from "@/components/admin/MultiImageUpload";
import { ShareToFacebook } from "@/components/admin/ShareToFacebook";
import { FacebookConnect } from "@/components/admin/FacebookConnect";
import { RichTextEditor } from "@/components/RichText";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-[#333333] mb-1">{label}</label>
    {children}
  </div>
);

const input = "w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";

export function SiteSettings() {
  const [site, setSite] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
  }, []);

  if (!site) return <p className="text-[#555555]">Loading…</p>;

  const set = (k, v) => setSite((s) => ({ ...s, [k]: v }));

  const addAnn = () =>
    set("announcements", [...(site.announcements || []), { id: crypto.randomUUID(), title: "", body: "", date: "" }]);
  const setAnn = (i, k, v) =>
    set("announcements", site.announcements.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));
  const removeAnn = (i) => set("announcements", site.announcements.filter((_, idx) => idx !== i));
  const dropAnn = (i) => {
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const arr = [...site.announcements];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    set("announcements", arr);
    setDragIdx(null);
    setOverIdx(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/site", site);
      toast.success("Site content saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="site-settings">
      <div className="flex justify-end">
        <button onClick={save} disabled={busy} data-testid="save-site-top-btn" className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
          <Save className="h-5 w-5" /> {busy ? "Saving…" : "Save all site content"}
        </button>
      </div>
      <FacebookConnect />

      <section className="bg-white rounded-lg border border-[#E5E7EB] p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg">Home Hero</h3>
        <Field label="Hero title"><input className={input} value={site.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} data-testid="hero-title-input" /></Field>
        <Field label="Hero subtitle"><textarea className={input} rows={2} value={site.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} /></Field>
        <MultiImageUpload
          label="Hero background images (rotate automatically)"
          value={site.hero_images?.length ? site.hero_images : (site.hero_image ? [site.hero_image] : [])}
          onChange={(v) => set("hero_images", v)}
          testid="hero-images"
        />
      </section>

      <section className="bg-white rounded-lg border border-[#E5E7EB] p-6 space-y-3">
        <h3 className="font-heading font-bold text-lg">This Week&apos;s Spotlight</h3>
        <p className="text-xs text-[#555555]">Shows a row of current division leaders (top of each active division&apos;s standings) on the Home page. Updates automatically with FargoRate syncs — no manual editing needed.</p>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="spotlight-toggle">
          <input
            type="checkbox"
            checked={site.spotlight_enabled !== false}
            onChange={(e) => set("spotlight_enabled", e.target.checked)}
            className="h-4 w-4 accent-[#1E63C4]"
          />
          Show &quot;This Week&apos;s Spotlight&quot; on the Home page
        </label>
      </section>

      <section className="bg-white rounded-lg border border-[#E5E7EB] p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg">About</h3>
        <Field label="About title"><input className={input} value={site.about_title || ""} onChange={(e) => set("about_title", e.target.value)} /></Field>
        <Field label="About text"><RichTextEditor value={site.about_text || ""} onChange={(v) => set("about_text", v)} testid="about-text-input" placeholder="About the league — type or paste formatted text" /></Field>
        <MultiImageUpload
          label="About images (rotate automatically)"
          value={site.about_images?.length ? site.about_images : (site.about_image ? [site.about_image] : [])}
          onChange={(v) => set("about_images", v)}
          testid="about-images"
        />
      </section>

      <section className="bg-white rounded-lg border border-[#E5E7EB] p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg">Contact & Links</h3>
        <Field label="Contact intro (subtitle on the Contact page)"><textarea className={input} rows={2} value={site.contact_intro || ""} onChange={(e) => set("contact_intro", e.target.value)} data-testid="contact-intro-input" /></Field>
        <Field label="Contact email"><input className={input} value={site.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="contact-email-input" /></Field>
        <Field label="Contact phone (optional — shows a click-to-call card)"><input className={input} value={site.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} placeholder="e.g. 416-702-7032" data-testid="contact-phone-input" /></Field>
        <Field label="Region (shown on the Contact page)"><input className={input} value={site.contact_region || ""} onChange={(e) => set("contact_region", e.target.value)} placeholder="e.g. Trenton & the Quinte region, Ontario" data-testid="contact-region-input" /></Field>
        <Field label="Facebook URL"><input className={input} value={site.facebook_url || ""} onChange={(e) => set("facebook_url", e.target.value)} /></Field>
        <Field label="FargoRate URL"><input className={input} value={site.fargorate_url || ""} onChange={(e) => set("fargorate_url", e.target.value)} /></Field>
        <Field label="Parent league URL"><input className={input} value={site.parent_league_url || ""} onChange={(e) => set("parent_league_url", e.target.value)} /></Field>
        <Field label="Weekly giveaway note"><textarea className={input} rows={2} value={site.giveaway_note || ""} onChange={(e) => set("giveaway_note", e.target.value)} /></Field>
        <Field label="Footer tagline (shown under the logo in the site footer)"><textarea className={input} rows={2} value={site.footer_tagline || ""} onChange={(e) => set("footer_tagline", e.target.value)} data-testid="footer-tagline-input" /></Field>
      </section>

      <section className="bg-white rounded-lg border border-[#E5E7EB] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-lg">League News / Announcements</h3>
            <p className="text-xs text-[#555555]">Drag the handle to reorder. Order is reflected on the Home page.</p>
          </div>
          <button onClick={addAnn} className="inline-flex items-center gap-1 text-sm text-[#1E63C4] font-semibold" data-testid="add-announcement-btn">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
        {(site.announcements || []).map((a, i) => (
          <div
            key={a.id || i}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDrop={() => dropAnn(i)}
            data-testid={`announcement-row-${i}`}
            className={`flex items-start gap-3 border rounded-md p-4 transition-all ${
              overIdx === i && dragIdx !== null && dragIdx !== i ? "border-[#1E63C4] ring-2 ring-[#1E63C4]/30" : "border-[#E5E7EB]"
            } ${dragIdx === i ? "opacity-50" : ""}`}
          >
            <button
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              data-testid={`announcement-drag-${i}`}
              className="mt-2.5 cursor-grab active:cursor-grabbing text-[#999999] hover:text-[#1E63C4]"
              aria-label="Drag to reorder"
            >
              <GripVertical className="h-5 w-5" />
            </button>
            <div className="flex-1 space-y-2">
              <div className="flex gap-2">
                <input className={input} placeholder="Title" value={a.title} onChange={(e) => setAnn(i, "title", e.target.value)} />
                <input className="w-40 rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm" placeholder="Date" value={a.date} onChange={(e) => setAnn(i, "date", e.target.value)} />
                <button onClick={() => removeAnn(i)} className="text-red-500 px-2" data-testid={`remove-announcement-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <RichTextEditor key={a.id || i} value={a.body} onChange={(v) => setAnn(i, "body", v)} testid={`announcement-body-${i}`} placeholder="Body — type or paste formatted text" />
              <ShareToFacebook
                defaultCaption={[a.title, a.body].filter(Boolean).join("\n\n")}
                source="announcement"
                testid={`fb-announcement-${i}`}
              />
            </div>
          </div>
        ))}
      </section>

      <button
        onClick={save}
        disabled={busy}
        data-testid="save-site-btn"
        className="inline-flex items-center gap-2 bg-[#1E63C4] text-white rounded-md px-6 py-3 font-semibold hover:bg-[#174e9c] disabled:opacity-60"
      >
        <Save className="h-5 w-5" /> {busy ? "Saving…" : "Save all site content"}
      </button>
    </div>
  );
}
